import type {ChangeEvent} from 'react';

import {useThemeColors} from '@nfq/colors';
import {useFormField, type ValidationFunc} from '@nfq/react-form';

/**
 * A custom hook for managing an input field's state and change handling.
 * This hook integrates with form management by providing a controlled state for an input field.
 * It ensures that the field's value is maintained and calls an external `onChange` handler when the value updates.
 *
 * @param name          The name of the input field.
 * @param initialValue  The initial value of the input field.
 * @param keepOnUnmount A boolean flag indicating whether the field's value should persist after unmounting.
 * @param onChange      A callback function triggered when the input value changes.
 * @param validation    An optional validation function for the input value.
 * @returns An object containing:
 * - `handleChange`: A function to handle input field changes.
 * - `value`: The current value of the input field.
 *
 * @example
 * ```tsx
 * const { handleChange, value } = useInputField("username", "", true, (val) => console.log(val));
 *
 * <input type="text" name="username" value={value} onChange={handleChange} />
 * ```
 */
export const useTextInput = (
    name: string,
    initialValue: string,
    keepOnUnmount: boolean,
    onChange: (value: string) => void,
    validation?: ValidationFunc
) => {
    const colors = useThemeColors();
    const {error, handleFieldChange, value} = useFormField(name, initialValue, initialValue, validation, keepOnUnmount);

    /**
     * Event handler for handling change events on an input element.
     *
     * @param event The change event.
     */
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleFieldChange(event.target.value);
        onChange(event.target.value);
    };

    return {
        colors,
        error,
        handleChange,
        value
    };
};